import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import AuthContext from "../context/AuthContext";
import NotificationBell from "../Components/NotificationBell";
import MessagePreview from "../Components/MessagePreview";
// icons for the mobile menu
import { FiMenu, FiX, FiUser, FiGrid, FiSettings, FiLogOut } from "react-icons/fi";
import logo from "../assets/taskonnect logo.png";

import {successAnimationData} from "../assets/Lottie-animations/successAnimation";

// --- Helper Component for Animated Links ---
const NavLinkItem = ({ to, children }) => {
  const baseClasses =
    "relative text-gray-900 font-medium py-2 px-3 transition-all duration-300 hover:text-blue-600 hover:scale-105";

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `${baseClasses} ${isActive ? "text-blue-600" : ""}`
      }
    >
      {({ isActive }) => (
        <>
          {children}
          <span
            className={`absolute -top-1 left-0 right-0 mx-auto h-0.5 bg-blue-600 transform transition-transform duration-300 ease-out
              ${isActive ? "scale-x-100" : "scale-x-0"}
            `}
            style={{ transformOrigin: "center" }}
          />
        </>
      )}
    </NavLink>
  );
};

// --- NEW: Profile Dropdown Menu ---
const ProfileMenu = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return <FiUser />;
  };

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef]);

  const closeMenu = () => setIsOpen(false);

  return (
    <div className="relative" ref={menuRef}>
      {/* Profile Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-blue-600 text-white rounded-full w-9 h-9 flex items-center justify-center font-bold text-sm hover:bg-blue-700 transition duration-150 shadow-md"
      >
        {getUserInitial()}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
          <div className="p-4 border-b border-gray-100">
            <p className="font-semibold text-gray-800 truncate">{user?.name || "User"}</p>
            <p className="text-sm text-gray-500 truncate">{user?.email || "No email"}</p>
          </div>
          <nav className="py-2">
            <Link
              to="/activity-center?section=profile&sub=profile_details"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
            >
              <FiUser className="w-4 h-4" />
              <span>View Profile</span>
            </Link>
            <Link
              to="/activity-center?section=activity&sub=activity_tickets"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
            >
              <FiGrid className="w-4 h-4" />
              <span>Activity</span>
            </Link>
            <Link
              to="/activity-center?section=settings&sub=settings_password"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
            >
              <FiSettings className="w-4 h-4" />
              <span>Account Settings</span>
            </Link>
          </nav>
          <div className="p-2 border-t border-gray-100">
            <button
              onClick={onLogout}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
            >
              <FiLogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


// --- Main Navbar Component ---
const Navbar = () => {
  const { authData, user, logoutUser } = useContext(AuthContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const role = authData?.role;
  const isLoggedIn = !!user;

  useEffect(() => {
    if (!isLoggedIn && isLoggingOut) {
      setIsLoggingOut(false);
    }
  }, [isLoggedIn, isLoggingOut]);

  const handleLogout = () => {
    setIsLoggingOut(true);
    setTimeout(() => {
      logoutUser();
      navigate("/");
    }, 1500);
  };

  const authButtonBase =
    "py-2 px-6 rounded-lg cursor-pointer font-bold transition-colors duration-200 text-base shadow-md whitespace-nowrap";

  // --- Link Render Helpers ---

  const renderCenterLinks = () => (
    <>
      {isLoggedIn && role === "user" && (
        <>
          <NavLinkItem to="/post-task">Post Task</NavLinkItem>
          <NavLinkItem to="/services">Services</NavLinkItem>
          <NavLinkItem to="/my-posts">My Posts</NavLinkItem>
        </>
      )}
      {isLoggedIn && role === "tasker" && (
        <>
          <NavLinkItem to="/browse-tasks">Browse Tasks</NavLinkItem>
          <NavLinkItem to="/my-tasks">My Tasks</NavLinkItem>
        </>
      )}
    </>
  );

  const renderLeftLinks = () => (
    <>
      {!isLoggedIn && (
        <>
          <NavLinkItem to="/how-it-works">How it Works</NavLinkItem>
          <NavLinkItem to="/reviews">Reviews</NavLinkItem>
        </>
      )}
    </>
  );

  const renderRightLinks = () => (
    <>
      {isLoggedIn && (
        <>
          <MessagePreview />
          <NavLinkItem to="/support">Support</NavLinkItem>
        </>
      )}
    </>
  );

  // --- Auth Button Render Helpers ---
  
  const renderDesktopAuth = () => (
    <>
      {isLoggedIn ? (
        <>
          <div className="relative text-gray-900 transition-colors duration-300 hover:text-blue-600 cursor-pointer">
            <NotificationBell />
          </div>
          <ProfileMenu user={user} onLogout={handleLogout} />
        </>
      ) : (
        <>
          <Link
            to="/auth"
            className="text-gray-900 text-base font-bold transition-colors duration-200 hover:text-blue-600"
          >
            Sign Up
          </Link>
          <Link
            to="/auth"
            className="text-gray-900 text-base font-bold transition-colors duration-200 hover:text-blue-600"
          >
            Login
          </Link>
          <Link
            to="/auth" 
            className={`${authButtonBase} bg-blue-600 text-white hover:bg-blue-700`}
          >
            Become a Tasker
          </Link>
        </>
      )}
    </>
  );

  const renderMobileAuth = () => (
    <>
      {isLoggedIn ? (
        <>
          <div className="flex items-center space-x-4">
            <div className="relative text-gray-900 transition-colors duration-300 hover:text-blue-600 cursor-pointer">
              <NotificationBell />
            </div>
            {/* --- Use ProfileMenu --- */}
            <ProfileMenu user={user} onLogout={handleLogout} />
          </div>
        </>
      ) : (
        <>
          <Link
            to="/auth"
            className="text-gray-900 font-bold hover:text-blue-600 text-base"
          >
            Sign Up
          </Link>
          <Link
            to="/auth"
            className="text-gray-900 font-bold hover:text-blue-600 text-base"
          >
            Login
          </Link>
          <Link
            to="/become-tasker" // Note: This link is different from your desktop one
            className={`${authButtonBase} bg-blue-600 text-white hover:bg-blue-700 w-1/2 text-center`}
          >
            Become a Tasker
          </Link>
        </>
      )}
    </>
  );

  return (
    <>
      <nav
        className="flex flex-wrap justify-between items-center px-4 md:px-8 h-auto md:h-[60px]
                 bg-[#cdc6dd]/60 backdrop-blur-md text-gray-900
                 sticky top-0 z-50 shadow-lg w-full py-3"
      >
        {/* --- LEFT SECTION --- */}
        <div className="flex items-center space-x-2 md:space-x-4">
          <Link
            to="/"
            className="flex items-center transition-opacity duration-200 hover:opacity-80 flex-shrink-0"
            onClick={() => setIsMenuOpen(false)}
          >
            <img
              className="h-12 w-auto"
              src={logo}
              alt="Taskonnect Logo"
            />
          </Link>
          <div className="hidden md:flex items-center space-x-2">
            {renderLeftLinks()}
          </div>
        </div>

        {/* --- CENTER SECTION --- */}
        <div className="hidden md:flex flex-grow justify-center items-center space-x-2">
          {renderCenterLinks()}
        </div>

        {/* --- RIGHT SECTION --- */}
        <div className="flex items-center space-x-2 md:space-x-4 flex-shrink-0">
          <div className="hidden md:flex items-center space-x-2">
            {renderRightLinks()}
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {renderDesktopAuth()}
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-2xl text-gray-900"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        {/* === MOBILE MENU: Dropdown === */}
        <div
          className={`w-full md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
            isMenuOpen
              ? "max-h-screen mt-4 pt-4 border-t border-gray-400/50"
              : "max-h-0"
          }`}
        >
          <div
            className="flex flex-col items-center space-y-4"
            onClick={() => setIsMenuOpen(false)}
          >
            {renderLeftLinks()}
            {renderCenterLinks()}
            {renderRightLinks()}

            <div className="flex flex-col items-center space-y-4 pt-4 w-full">
              {renderMobileAuth()}
            </div>
          </div>
        </div>
      </nav>

      {/* --- Logout Notification --- */}
      {isLoggingOut && (
        <div
          className="fixed top-20 right-4 z-[9999] p-4 bg-white rounded-lg shadow-xl border border-gray-200
                   flex items-center space-x-3"
        >
          <div className="w-12 h-12">
            <Lottie
              animationData={successAnimationData}
              loop={false}
            />
          </div>
          <span className="font-semibold text-gray-800 text-lg">
            Log out successful, redirecting...
          </span>
        </div>
      )}
    </>
  );
};

export default Navbar;
